import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  getMerchantCategories,
  createMerchantCategory,
  getMerchants,
  getMerchant,
  createMerchant,
  updateMerchant,
  updateMerchantStatus,
  CreateMerchantPayload,
  GetMerchantsParams,
  MerchantCategory,
  MerchantStatus,
  getAdminCategories,
  updateCategoryStatus,
  updateCategoryName,
  deleteCategory,
  GetAdminCategoriesParams,
  getCouponSales,
  GetCouponSalesParams,
  MonthlySalesParams,
  getMerchantMonthlySales,
  deleteMerchant,
  updateCouponStatus,
  getMerchantCommissionReport,
} from '@/api/vendors';

export function useMerchantCategories() {
  return useQuery({
    queryKey: ['merchant-categories'],
    queryFn: getMerchantCategories,
    staleTime: Infinity,
  });
}

export function useCreateMerchantCategory() {
  const queryClient = useQueryClient();

  return useMutation<MerchantCategory, AxiosError<{ message?: string }>, { name: string }>({
    mutationFn: createMerchantCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-categories'] });
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
    },
  });
}

export function useMerchants(params: GetMerchantsParams) {
  return useQuery({
    queryKey: ['merchants', params],
    queryFn: () => getMerchants(params),
    placeholderData: (previousData) => previousData,
  });
}

export function useMerchant(merchantId: number) {
  return useQuery({
    queryKey: ['merchant', merchantId],
    queryFn: () => getMerchant(merchantId),
    enabled: Number.isFinite(merchantId),
  });
}

export function useCreateMerchant() {
  const queryClient = useQueryClient();

  return useMutation<unknown, AxiosError<{ message?: string }>, CreateMerchantPayload>({
    mutationFn: createMerchant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchants'] });
    },
  });
}

export function useUpdateMerchant() {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    AxiosError<{ message?: string }>,
    { merchantId: number; payload: CreateMerchantPayload }
  >({
    mutationFn: ({ merchantId, payload }) => updateMerchant(merchantId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchants'] });
    },
  });
}

export function useUpdateMerchantStatus() {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    AxiosError<{ message?: string }>,
    { merchantId: number; status: MerchantStatus }
  >({
    mutationFn: ({ merchantId, status }) => updateMerchantStatus(merchantId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchants'] });
    },
  });
}

export function useDeleteMerchant() {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError<{ message?: string }>, number>({
    mutationFn: deleteMerchant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchants'] });
    },
  });
}

export function useAdminCategories(params: GetAdminCategoriesParams) {
  return useQuery({
    queryKey: ['admin-categories', params],
    queryFn: () => getAdminCategories(params),
    placeholderData: (previousData) => previousData,
  });
}

export function useUpdateCategoryStatus() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError<{ message?: string }>,
    { categoryId: number; isActive: boolean }
  >({
    mutationFn: ({ categoryId, isActive }) => updateCategoryStatus(categoryId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      queryClient.invalidateQueries({ queryKey: ['merchant-categories'] });
    },
  });
}

export function useUpdateCategoryName() {
  const queryClient = useQueryClient();

  return useMutation<
    MerchantCategory,
    AxiosError<{ message?: string }>,
    { categoryId: number; name: string }
  >({
    mutationFn: ({ categoryId, name }) => updateCategoryName(categoryId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      queryClient.invalidateQueries({ queryKey: ['merchant-categories'] });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError<{ message?: string }>, number>({
    mutationFn: (categoryId) => deleteCategory(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      queryClient.invalidateQueries({ queryKey: ['merchant-categories'] });
    },
  });
}

export function useCouponSales(params: GetCouponSalesParams) {
  return useQuery({
    queryKey: ['coupon-sales', params],
    queryFn: () => getCouponSales(params),
    placeholderData: (previousData) => previousData,
  });
}

export function useMerchantMonthlySales(merchantId: number, params: MonthlySalesParams) {
  return useQuery({
    queryKey: ['merchant-monthly-sales', merchantId, params],
    queryFn: () => getMerchantMonthlySales(merchantId, params),
    enabled: merchantId > 0 && !!params.month && Number.isFinite(params.year),
    placeholderData: (previousData) => previousData,
  });
}

export function useUpdateCouponStatus() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError<{ message?: string }>,
    { couponId: number; status: 'Active' | 'Inactive'; merchantId: number }
  >({
    mutationFn: ({ couponId, status }) => updateCouponStatus(couponId, status),
    onSuccess: (_, { merchantId }) => {
      queryClient.invalidateQueries({ queryKey: ['merchant', merchantId] });
    },
  });
}

export function useMerchantCommissionReport(merchantId?: number, enabled = true) {
  return useQuery({
    queryKey: ['merchant-commission-report', merchantId],
    queryFn: () => getMerchantCommissionReport(merchantId!),
    enabled: typeof merchantId === 'number' && merchantId > 0 && enabled,
  });
}
